import { AnalyticsStore, ContextStore, ExperimentStore } from 'bernie-plugin-mobx';
import { inject, observer } from 'mobx-react';
import { isEqual } from 'lodash';
import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import * as React from 'react';

import { updateSearch } from 'bernie-core';

import { PropertyListing } from '@shared-ui/lodging-property-listing';
import { useRefContext } from '@shared-ui/ref-context';

import { CurrentHotelStore } from 'src/common/stores/current-hotel-store';
import { getPropertySearchInput } from 'stores/adapters';
import { MultiItemStore } from 'stores/multi-item-store';
import { PageLayoutStore } from 'src/common/stores/page-layout/page-layout-store';
import { SearchCriteriaModel } from 'src/common/stores/models/search-criteria-model';
import { SearchResultsStore } from 'stores/search-results-store';
import { UIPageStore } from 'stores/ui-page-store';
import { useRefProvider } from 'src/common/stores/contexts/ref-provider';

import { PaginationSubSets } from 'models';
import { PROPERTY_LISTING_BATCH_KEY } from 'src/common/constants/batchKeys';

import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkLoader } from 'uitk-react-loader';

import { getDateFlexibility } from '../../common/universal-search-form/universal-search-form-utils';

import { DatePeriodInput } from 'src/common/__generated__/api';
import useSecondaryFetchStatus from './use-secondary-fetch-status-hook';

export interface PropertyListingWrapperProps {
  context?: ContextStore;
  multiItem?: MultiItemStore;
  searchResults?: SearchResultsStore;
  experiment?: ExperimentStore;
  uiPage?: UIPageStore;
  currentHotel?: CurrentHotelStore;
  searchCriteria?: SearchCriteriaModel;
  analytics?: AnalyticsStore;
  searchInputs?: any;
  criteriaChanged?: boolean;
  pageLayout?: PageLayoutStore;
}

export const PropertyListingWrapper = inject(
  'context',
  'multiItem',
  'uiPage',
  'currentHotel',
  'searchCriteria',
  'searchResults',
  'experiment',
  'analytics',
  'pageLayout'
)(
  observer(
    ({
      analytics,
      context,
      uiPage,
      multiItem,
      currentHotel,
      searchCriteria,
      searchResults,
      experiment,
      searchInputs,
      criteriaChanged,
      pageLayout,
    }: PropertyListingWrapperProps) => {
      const [inputs, setInputs] = useState(searchInputs);
      const { getTargets } = useRefContext();
      const { SearchForm: searchFormRef } = getTargets(['SearchForm']);

      const location = useLocation();
      const history = useHistory();

      useEffect(() => {
        if (criteriaChanged) {
          setInputs(searchInputs);
        }
      }, [criteriaChanged]);

      if (!context || !searchCriteria || !searchResults?.searchCriteria) return null;
      const multiItemContext = multiItem?.multiItemContext || null;

      const showLoadingSpinner = useSecondaryFetchStatus({ searchResults, context, multiItem, uiPage, analytics, experiment });

      const openDatepicker = (elementId?: string) => {
        if (elementId === 'propertySelectDateCard') {
          const searchCriteriaInResults = searchResults?.searchCriteria;
          if (searchCriteriaInResults) {
            searchCriteriaInResults.dateSelectedByUser = true;
          }
        }
        uiPage?.dispatchOpenNewDatePickerEvent();
      };

      const changeFlexibility = (flexibility: DatePeriodInput[]) => {
        let currentFlexibilityParams =
          typeof searchCriteria.flexibility === 'string' ? [searchCriteria.flexibility] : searchCriteria.flexibility;
        // This could be a MobX "array", so it gets converted to a real one for comparison
        if (currentFlexibilityParams) {
          currentFlexibilityParams = [...currentFlexibilityParams];
        }
        const newFlexibilityParams = getDateFlexibility(flexibility);

        if (!isEqual(currentFlexibilityParams, newFlexibilityParams)) {
          searchFormRef?.current?.focus();
          searchFormRef?.current?.scrollIntoView({ behavior: 'smooth' });

          const newParamsObject = new URLSearchParams();
          newFlexibilityParams.forEach(param => newParamsObject.append('flexibility', param));

          updateSearch({ location, history, newParams: newParamsObject.toString() });
        }
      };

      const batchKey = experiment?.exposures?.SHOPPING_PWA_BATCHING_SRP_QUERY?.bucket === 1 ? PROPERTY_LISTING_BATCH_KEY : undefined;

      // Temporarily passing the PWA search listings response to the shared UI component to avoid duplicating the
      // request for search listings on SRP. Once we migrate the listings fetch to shared UI this will be cleaned up.
      const useSharedUIListingsQuery =
        !multiItem?.isPackageShoppingPath() && experiment?.exposures?.EG_Property_Search_Query_Migration?.bucket === 1;
      const propertyListingControlInputs = () => {
        const {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          context: bexApiContext,
          ...rest
        } = getPropertySearchInput(context, searchResults.searchCriteria, multiItemContext);

        return {
          ...rest,
          rooms: searchResults.searchCriteria.rooms,
        };
      };

      const searchListingsProps = useSharedUIListingsQuery
        ? {
            inputs,
          }
        : {
            inputs: propertyListingControlInputs(),
            isLoadingPwa: searchResults.isLoading || searchResults.isPaginationLoading,
            propertySearchListingAdaptexAnalyticsEvents: searchResults.propertyListingAdaptexAnalyticsSuccessEvents as any,
            propertySearchListingsPwa: searchResults.propertySearchListings as any,
          };

      const isVrbexSplitView = pageLayout?.templateName === 'SPLIT_VIEW';
      const [searchResultsListRef] = useRefProvider('searchResultsList');

      return (
        <>
          <PropertyListing
            {...searchListingsProps}
            batchKey={batchKey}
            openDatePicker={openDatepicker}
            onClickCallback={(lodgingCard: { id: string }) => {
              currentHotel?.setPropertyListingAsCurrentHotel(lodgingCard);
            }}
            onHoverChanged={
              isVrbexSplitView
                ? (id: string, hovered: boolean) => {
                    searchCriteria.setHighlightedPropertyId(hovered ? id : '');
                  }
                : undefined
            }
            onPaginationCallback={
              isVrbexSplitView
                ? (paginationSubSets: PaginationSubSets) => {
                    searchCriteria.paginationSubSets = paginationSubSets;
                  }
                : undefined
            }
            shouldScrollToTopOnSearch={isVrbexSplitView}
            scrollToTopRef={searchResultsListRef}
            changeFlexibility={changeFlexibility}
          />
          {!useSharedUIListingsQuery && showLoadingSpinner && (
            <UitkLayoutFlex justifyContent="center">
              <UitkLayoutFlexItem>
                <UitkLoader />
              </UitkLayoutFlexItem>
            </UitkLayoutFlex>
          )}
        </>
      );
    }
  )
);
